import { Component, ViewChild, TemplateRef, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { AnimationEvent } from '@angular/animations';
import { TemplatePortal } from '@angular/material';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
export type LoadingType = number;
export let LoadingType: any = {};
LoadingType.Circular =  /** @type {?} */(( <any>'circular'));
LoadingType.Linear =  /** @type {?} */(( <any>'linear'));
LoadingType[LoadingType.Circular] = "Circular";
LoadingType[LoadingType.Linear] = "Linear";

export type LoadingMode = number;
export let LoadingMode: any = {};
LoadingMode.Determinate =  /** @type {?} */(( <any>'determinate'));
LoadingMode.Indeterminate =  /** @type {?} */(( <any>'indeterminate'));
LoadingMode[LoadingMode.Determinate] = "Determinate";
LoadingMode[LoadingMode.Indeterminate] = "Indeterminate";

export type LoadingStrategy = number;
export let LoadingStrategy: any = {};
LoadingStrategy.Overlay =  /** @type {?} */(( <any>'overlay'));
LoadingStrategy.Replace =  /** @type {?} */(( <any>'replace'));
LoadingStrategy[LoadingStrategy.Overlay] = "Overlay";
LoadingStrategy[LoadingStrategy.Replace] = "Replace";

export type LoadingStyle = number;
export let LoadingStyle: any = {};
LoadingStyle.FullScreen =  /** @type {?} */(( <any>'fullscreen'));
LoadingStyle.Overlay =  /** @type {?} */(( <any>'overlay'));
LoadingStyle.None =  /** @type {?} */(( <any>'none'));
LoadingStyle[LoadingStyle.FullScreen] = "FullScreen";
LoadingStyle[LoadingStyle.Overlay] = "Overlay";
LoadingStyle[LoadingStyle.None] = "None";


import { TdFadeInOutAnimation } from '../common/common.module';
export class TdLoadingComponent {
private _animationIn: Subject<any> = new Subject<any>();
private _animationOut: Subject<any> = new Subject<any>();
private _mode: LoadingMode = LoadingMode.Indeterminate;
private _defaultMode: LoadingMode = LoadingMode.Indeterminate;
private _value: number = 0;
/**
 * Flag for animation
 */
animation: boolean = false;
/**
 * Content injected into loading component.
 */
content: TemplatePortal;
/**
 * Sets mode of [TdLoadingComponent] to LoadingMode.Determinate or LoadingMode.Indeterminate
 * @param {?} mode
 * @return {?}
 */
set mode(mode: LoadingMode) {
    this._defaultMode = mode;
  }
/**
 * @return {?}
 */
get mode(): LoadingMode {
    return this._mode;
  }
/**
 * Sets value of [TdLoadingComponent] if mode is 'LoadingMode.Determinate'
 * @param {?} value
 * @return {?}
 */
set value(value: number) {
    this._value = value;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
  }
/**
 * @return {?}
 */
get value(): number {
    return this._value;
  }

  style: LoadingStyle = LoadingStyle.None;
/**
 * height: number
 * Sets height of [TdLoadingComponent].
 */
height: number;
/**
 * type: LoadingType
 * Sets type of [TdLoadingComponent] rendered.
 */
type: LoadingType = LoadingType.Circular;
/**
 * color: primary' | 'accent' | 'warn'
 * Sets theme color of [TdLoadingComponent] rendered.
 */
color: 'primary' | 'accent' | 'warn' = 'primary';
/**
 * @param {?} _changeDetectorRef
 */
constructor(private _changeDetectorRef: ChangeDetectorRef) {}
/**
 * @return {?}
 */
getHeight(): string {
    // Ignore height if style is `overlay` or `fullscreen`.
    // Add height if child elements have a height and style is `none`, else return default height.
    if (this.isOverlay() || this.isFullScreen()) {
      return undefined;
    } else {
      return this.height ? `${this.height}px` : '150px';
    }
  }
/**
 * @return {?}
 */
getCircleDiameter(): string {
    if (this.height) {
      let /** @type {?} */ diameter: number = this.height * (2 / 3);
      if (diameter < 80) {
        return `${diameter}px`;
      }
    }
    return '80px';
  }
/**
 * @return {?}
 */
isCircular(): boolean {
    return this.type === LoadingType.Circular;
  }
/**
 * @return {?}
 */
isLinear(): boolean {
    return this.type === LoadingType.Linear;
  }
/**
 * @return {?}
 */
isFullScreen(): boolean {
    return this.style === LoadingStyle.FullScreen;
  }
/**
 * @return {?}
 */
isOverlay(): boolean {
    return this.style === LoadingStyle.Overlay;
  }
/**
 * @param {?} event
 * @return {?}
 */
animationComplete(event: AnimationEvent): void {
    // Check to see if its "in" or "out" animation to execute the proper callback
    if (!event.fromState) {
      this.inAnimationCompleted();
    } else {
      this.outAnimationCompleted();
    }
  }
/**
 * @return {?}
 */
inAnimationCompleted(): void {
    this._animationIn.next(undefined);
  }
/**
 * @return {?}
 */
outAnimationCompleted(): void {
   /* little hack to reset the loader value and animation before removing it from DOM
    * else, the loader will appear with prev value when its registered again
    * and will do an animation going prev value to 0.
    */
    this.value = 0;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
    setTimeout(() => {
      this._animationOut.next(undefined);
    });
  }
/**
 * Starts in animation and returns an observable for completition event.
 * @return {?}
 */
startInAnimation(): Observable<any> {
    setTimeout(() => {
      this.animation = true;
      // Check for changes for `OnPush` change detection
      this._changeDetectorRef.markForCheck();
    });
    /* need to switch back to the selected mode, so we have saved it in another variable
    *  and then recover it. (issue with protractor)
    */
    this._mode = this._defaultMode;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
    return this._animationIn.asObservable();
  }
/**
 * Starts out animation and returns an observable for completition event.
 * @return {?}
 */
startOutAnimation(): Observable<any> {
    this.animation = false;
    /* need to switch back and forth from determinate/indeterminate so the setInterval()
    * inside md-progress-spinner stops and protractor doesnt timeout waiting to sync.
    */
    this._mode = LoadingMode.Determinate;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
    return this._animationOut.asObservable();
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'td-loading',
  styleUrls: ['./loading.component.css' ],
  templateUrl: './loading.component.html',
  animations: [
    TdFadeInOutAnimation(),
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
];
}

function TdLoadingComponent_tsickle_Closure_declarations() {
/** @type {?} */
TdLoadingComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TdLoadingComponent.ctorParameters;
/** @type {?} */
TdLoadingComponent.prototype._animationIn;
/** @type {?} */
TdLoadingComponent.prototype._animationOut;
/** @type {?} */
TdLoadingComponent.prototype._mode;
/** @type {?} */
TdLoadingComponent.prototype._defaultMode;
/** @type {?} */
TdLoadingComponent.prototype._value;
/**
 * Flag for animation
 * @type {?}
 */
TdLoadingComponent.prototype.animation;
/**
 * Content injected into loading component.
 * @type {?}
 */
TdLoadingComponent.prototype.content;
/** @type {?} */
TdLoadingComponent.prototype.style;
/**
 * height: number
 * Sets height of [TdLoadingComponent].
 * @type {?}
 */
TdLoadingComponent.prototype.height;
/**
 * type: LoadingType
 * Sets type of [TdLoadingComponent] rendered.
 * @type {?}
 */
TdLoadingComponent.prototype.type;
/**
 * color: primary' | 'accent' | 'warn'
 * Sets theme color of [TdLoadingComponent] rendered.
 * @type {?}
 */
TdLoadingComponent.prototype.color;
/** @type {?} */
TdLoadingComponent.prototype._changeDetectorRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
